
import { useEffect, useRef, useState } from 'react';

export default function IntelligenceMark() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const columns = [
    {
      icon: 'ri-arrow-right-down-line',
      title: 'Input',
      description:
        'Smart capture of inquiries, patient leads, case submissions, and service requests.',
    },
    {
      icon: 'ri-brain-line',
      title: 'Intelligence',
      description:
        'AI-driven qualification, routing, scheduling, and workflow optimization.',
    },
    {
      icon: 'ri-arrow-right-up-line',
      title: 'Output',
      description:
        'Improved close rates, faster response times, better client experience.',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-28 bg-[#E8E3DA] relative overflow-hidden"
    >
      {/* Subtle background texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, #0E1A2B 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      ></div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-20 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute -inset-6 bg-[#2FB8AC]/10 rounded-full blur-xl"></div>
              <div className="relative w-24 h-24 rounded-full bg-white/80 backdrop-blur-sm shadow-lg flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/5f45847157c596b0dd69fa68df90550f.png"
                  alt="Intelligence Mark"
                  className="h-16 w-16"
                />
              </div>
            </div>
          </div>
          <h2
            className="text-3xl md:text-4xl font-semibold text-[#0E1A2B] mb-4"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
          >
            Automation with accountability.
          </h2>
          <p className="text-base text-gray-700 max-w-2xl mx-auto">
            Intelligent systems, structured oversight, measurable results.
          </p>
        </div>

        {/* Three Columns with connecting flow */}
        <div className="relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-12 left-[16.67%] right-[16.67%] h-px bg-gradient-to-r from-transparent via-[#2FB8AC]/40 to-transparent"></div>

          <div className="grid md:grid-cols-3 gap-10">
            {columns.map((col, index) => (
              <div
                key={index}
                className={`text-center transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${200 + index * 200}ms` }}
              >
                <div className="mb-8 flex justify-center">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-white shadow-lg flex items-center justify-center border border-[#2FB8AC]/20">
                      <i className={`${col.icon} text-3xl text-[#2FB8AC]`}></i>
                    </div>
                    {index < 2 && (
                      <div className="hidden md:flex absolute top-1/2 -right-14 -translate-y-1/2 w-8 h-8 items-center justify-center">
                        <i className="ri-arrow-right-line text-[#2FB8AC]/50 text-lg"></i>
                      </div>
                    )}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-[#0E1A2B] mb-3">
                  {col.title}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed max-w-xs mx-auto">
                  {col.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
